import React, { useState } from 'react';

import { useTheme } from '@mui/material';
import { compact, forEach, get, isEmpty, join, map, omit, replace, toString } from 'lodash';
import { GOOGLE_MAPS_API_KEY, DEFAULT_LOCATION } from '../config';
import { mapDarkTheme, mapLightTheme } from '../utils/theme';

export type Location = {
  lat: number;
  lng: number;
};

export type Marker = {
  location: Location;
  icon?: string;
  size?: 'tiny' | 'mid' | 'small';
  scale?: 1 | 2 | 4;
  color?: string;
  label?: string;
};

export type GoogleStaticMapsProps = {
  center: Location;
  zoom: number;
  markers?: Marker[];
  imageSize?: { width: number; height: number };
  scale?: 1 | 2;
  language?: string;
  disableTheme?: boolean;
  disablePlaceholder?: boolean;
  mapStaticParams?: Record<string, string>;
} & React.ImgHTMLAttributes<HTMLImageElement>;

const BASE_URL = 'https://maps.googleapis.com/maps/api/staticmap';

const DEFAULT_IMAGE_SIZE = { width: 600, height: 600 };
const DEFAULT_SCALE = 1;

const locationToLocationParam = (location: Location) => `${location.lat},${location.lng}`;

const markerToMarkerParam = (marker: Marker) => {
  const markerOptions = omit(marker, 'location');
  const markerParts = map(markerOptions, (value, key) => `${key}:${value}`) ?? [];

  const locationPart = locationToLocationParam(marker.location);
  markerParts.push(locationPart);

  return join(markerParts, '|');
};

const mapStyleToMapStyleParam = (mapStyle: google.maps.MapTypeStyle) => {
  const featurePart = mapStyle.featureType ? `feature:${mapStyle.featureType}` : null;
  const elementPart = mapStyle.elementType ? `element:${mapStyle.elementType}` : null;

  const color: string | undefined = get(mapStyle, 'stylers[0].color');
  let colorPart = null;
  if (color) {
    const formattedColor = `0x${replace(color, '#', '')}`;
    colorPart = `color:${formattedColor}`;
  }

  const styleParts = compact([featurePart, elementPart, colorPart]);
  return join(styleParts, '|');
};

const getPlaceholderImg = (isDarkMode: boolean, disableMarkers: boolean) => {
  if (isDarkMode && disableMarkers) {
    return '/static/maps_democracy_monument_zoom12_dark.png';
  }
  if (isDarkMode && !disableMarkers) {
    return '/static/maps_democracy_monument_zoom12_dark_marker.png';
  }
  if (!isDarkMode && disableMarkers) {
    return '/static/maps_democracy_monument_zoom12.png';
  }
  if (!isDarkMode && !disableMarkers) {
    return '/static/maps_democracy_monument_zoom12_marker.png';
  }
  return '/static/maps_democracy_monument_zoom12_marker.png';
};

// Google Maps Static API component, see https://developers.google.com/maps/documentation/maps-static
const GoogleStaticMaps = ({
  center,
  zoom,
  imageSize,
  scale = DEFAULT_SCALE,
  markers,
  language = 'th',
  disableTheme,
  disablePlaceholder,
  mapStaticParams,
  style,
  ...imgProps
}: GoogleStaticMapsProps) => {
  const theme = useTheme();
  const [disableMarkers, setDisableMarkers] = useState<boolean>(isEmpty(markers));

  const url = new URL(BASE_URL);

  // API Key
  url.searchParams.set('key', GOOGLE_MAPS_API_KEY ?? '');

  // Image size and scale, defaulting to 600x600, scale will affect final resolution
  const imageWidth = imageSize?.width ?? DEFAULT_IMAGE_SIZE.width;
  const imageHeight = imageSize?.height ?? DEFAULT_IMAGE_SIZE.height;
  url.searchParams.set('size', `${imageWidth}x${imageHeight}`);
  url.searchParams.set('scale', toString(scale));

  // Maps center location and zoom level
  url.searchParams.set('center', locationToLocationParam(center));
  url.searchParams.set('zoom', toString(zoom));

  const markerParams = map(markers, (marker) => markerToMarkerParam(marker));
  forEach(markerParams, (markerParam) => {
    url.searchParams.append('markers', markerParam);
  });

  // Other params
  url.searchParams.set('language', language);

  // Map styles
  const mapStyles = theme.palette.mode === 'dark' ? mapDarkTheme : mapLightTheme;
  if (!disableTheme) {
    forEach(mapStyles, (mapStyle) => {
      const mapStyleParam = mapStyleToMapStyleParam(mapStyle);
      url.searchParams.append('style', mapStyleParam);
    });
  }

  // Additional map static params in key-value form. Merge these in last to allow overrides of other params.
  if (!isEmpty(mapStaticParams)) {
    forEach(mapStaticParams, (value, key) => {
      url.searchParams.set(key, value);
    });
  }

  const isUsePlaceholder =
    !disablePlaceholder &&
    center.lat === DEFAULT_LOCATION.lat &&
    center.lng === DEFAULT_LOCATION.lng &&
    zoom === 12 &&
    imageWidth === 600 &&
    imageHeight === 300;

  const isDarkMode = !disableTheme && theme.palette.mode === 'dark';

  const imageSrc = isUsePlaceholder ? getPlaceholderImg(isDarkMode, !!disableMarkers) : url.toString();

  return (
    <img
      src={imageSrc}
      alt="Google Static Maps"
      width={imageWidth * scale}
      height={imageHeight * scale}
      style={{ width: '100%', height: '100%', objectFit: 'cover', ...style }}
      {...imgProps}
    />
  );
};

export default GoogleStaticMaps;
